import { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, Box, BrandLogo, Button } from '@ltvco/refresh-lib/theme';
import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  HeaderContainer,
  StyledContainer,
  StyledHeader,
  StyledHeaderNav,
  StyledBannerContainer,
} from './components';
import { ClassicHeaderContents } from './ClassicHeaderContents/ClassicHeaderContents';
import {
  BannerMigrationCta,
  FreemiumUpgradeBanner,
} from '@ltvco/refresh-lib/v2';
import { GoodhoodPromoBanner } from 'components/GoodhoodPromo';
import { useIpAddress } from '@ltvco/refresh-lib/hooks';
import { useIpToZip } from 'hooks/useIpToZip';
import { DallasZips } from 'components/GoodhoodPromo/constants';
import { useScreenSize } from '@ltvco/refresh-lib/utils';

const ButtonContainer = styled(Box)(({ theme }) => ({
  width: '55px',
  display: 'flex',
  flex: 1,

  button: {
    minWidth: 'inherit',
    padding: 0,
    height: '100%',
    width: '55px',

    '&:hover': {
      background: 'inherit',
    },
  },

  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    paddingLeft: '1rem',

    button: {
      width: 'inherit',
    },
  },
}));

const StyledButton = styled(Button)({
  height: 'fit-content',
  py: 1.875,
  backgroundColor: 'transparent',

  '&.MuiButtonBase-root:hover': {
    bgcolor: 'transparent',
  },
});

interface ClassicHeaderProps {
  /** Callback to handle logout */
  handleLogout: () => void;
}

export const ClassicHeader = ({ handleLogout }: ClassicHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  const { trackEventGA4 } = useContext(AppConfig);
  const { isFreePlan } = usePermissions();
  const { isMobile } = useScreenSize();

  const checkIfShouldSeeHeaderContents = () => {
    const routesWithoutHeaderContents = [
      '/vehicle-self-inspection',
      '/activate-free-access',
    ];

    return !routesWithoutHeaderContents.some((route: string) =>
      location?.pathname.includes(route),
    );
  };

  const [isGoodhoodBannerVisible, setIsGoodhoodBannerVisible] = useState(() => {
    const saved = localStorage.getItem('goodhoodBannerVisisble');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [isFreemiumBannerVisible, setIsFreemiumBannerVisible] = useState(
    isFreePlan ?? false,
  );

  const [shouldSeeHeaderContents, setShouldSeeHeaderContents] =
    useState<boolean>(checkIfShouldSeeHeaderContents());

  useEffect(() => {
    setShouldSeeHeaderContents(checkIfShouldSeeHeaderContents());
  }, [location?.pathname]);

  const ip = useIpAddress();
  const { data: cityStateLocationData } = useIpToZip(ip);
  const zipIsADallasZip = cityStateLocationData
    ? DallasZips.includes(cityStateLocationData?.entities?.locations[0]?.zip5)
    : false;

  const isOnLoginPage = location.pathname.includes('/login');
  const shouldRenderGoodhood =
    isGoodhoodBannerVisible && zipIsADallasZip && !isOnLoginPage;

  useEffect(() => {
    // Ensure freemium banner reflects isFreePlan once it resolves
    if (typeof isFreePlan === 'boolean') {
      setIsFreemiumBannerVisible(isFreePlan);
    }
  }, [isFreePlan]);

  useEffect(() => {
    if (location.pathname.includes('/bumper-chat')) {
      document.documentElement.style.setProperty('--header-height', '0px');
      return;
    }

    const styledHeader = document.getElementById('styled-header');
    if (!styledHeader) return;

    const updateHeaderHeight = () => {
      const height = styledHeader.offsetHeight;
      document.documentElement.style.setProperty(
        '--header-height',
        `${height}px`,
      );
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(styledHeader);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-height');
    };
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(
      'goodhoodBannerVisisble',
      JSON.stringify(isGoodhoodBannerVisible),
    );
  }, [isGoodhoodBannerVisible]);

  const handleBrandLogoClick = () => {
    trackEventGA4({
      event_name: 'header_engagement',
      type: 'nav - brand logo',
    });
    if (!shouldSeeHeaderContents) return null;

    navigate('/dashboard');
  };

  const handleFreemiumBannerClose = () => {
    setIsFreemiumBannerVisible(false);
  };

  const handleFreemiumBannerUpgrade = () => {
    navigate('/subscribe');
  };

  if (location.pathname.includes('/bumper-chat')) return null;

  return (
    <HeaderContainer ref={headerRef} id="header">
      <StyledContainer id="styled-header">
        <StyledBannerContainer>
          <BannerMigrationCta
            hasCustomMargin={zipIsADallasZip}
            hasCustomStyle={!zipIsADallasZip}
          />
          <GoodhoodPromoBanner
            isVisible={shouldRenderGoodhood}
            setIsVisible={setIsGoodhoodBannerVisible}
          />
        </StyledBannerContainer>
        <StyledHeader
          direction={'row'}
          justifyContent={'space-between'}
          id="main-header"
        >
          <StyledHeaderNav>
            <ButtonContainer>
              <StyledButton
                disabled={!shouldSeeHeaderContents}
                className="brand-logo-btn"
                disableRipple
                onClick={handleBrandLogoClick}
                data-testid="brand-logo-btn"
              >
                <BrandLogo />
              </StyledButton>
            </ButtonContainer>
            {shouldSeeHeaderContents && (
              <ClassicHeaderContents handleLogout={handleLogout} />
            )}
          </StyledHeaderNav>
        </StyledHeader>
        {isFreePlan && isFreemiumBannerVisible && (
          <StyledBannerContainer id="freemium-banner">
            <FreemiumUpgradeBanner
              isFreePlan={isFreePlan}
              isOpen={isFreemiumBannerVisible}
              onClose={handleFreemiumBannerClose}
              isMobile={isMobile}
              onUpgradeClick={handleFreemiumBannerUpgrade}
            />
          </StyledBannerContainer>
        )}
      </StyledContainer>
    </HeaderContainer>
  );
};
